import { formatDate } from '@angular/common';

import {
  ApprovalReviewCompetition,
  ApprovalReviewData,
  ApprovalReviewFreight,
  ApprovalReviewSupplier
} from '../../interfaces/approval-review';
import { QuotationMapItem } from '../../interfaces/quotation-map-item';
import { QuotationMapSupplier } from '../../interfaces/quotation-map-supplier';
import { QuotationMapSupplierFreight } from '../../interfaces/quotation-map-supplier-freight';

/**
 * Revisão da aprovação: o que vai ser aprovado, por fornecedor, com concorrência, frete e
 * economia. É a mesma conta no mapa de cotação (antes de mandar para aprovação, a partir da
 * seleção do comprador) e na tela de aprovação (a partir das cotações aguardando aprovação,
 * com `selecaoDaAprovacao`).
 */

/** O que a revisão lê: itens com as cotações marcadas (cotado ou negociado) e os fretes. */
export interface ApprovalReviewSource {
  purchaseId: string;
  items: QuotationMapItem[];
  supplierIds: string[];
  supplierFreights?: Map<string, QuotationMapSupplierFreight>;
}

export interface ApprovalReviewOptions {
  /** Cor do fornecedor pela posição na lista (a mesma do cabeçalho do mapa). */
  accentColor: (supplierIndex: number) => string;
  /** Locale das datas ("Vencida em 22/08/2026"). */
  locale: string;
}

/** Cores dos fornecedores no mapa e na revisão: [fundo do cabeçalho, barra]. */
export const SUPPLIER_HEADER_COLORS: ReadonlyArray<readonly [string, string]> = [
  ['#e6f0fe', '#1a73e8'],
  ['#fdf1d9', '#e0a100'],
  ['#f0e8fb', '#8a4fd8'],
  ['#def3f0', '#0f9d8a'],
  ['#fde8ee', '#d6336c'],
  ['#e9eef3', '#5b6b7c']
];

export function supplierHeaderColors(supplierIndex: number): { background: string; bar: string } {
  const [background, bar] = SUPPLIER_HEADER_COLORS[supplierIndex % SUPPLIER_HEADER_COLORS.length];
  return { background, bar };
}

type Selection = Pick<QuotationMapSupplier, 'selected' | 'negotiatedSelected' | 'negotiatedPrice'>;

/** Seleção que tem preço: cotado marcado, ou negociado marcado com valor (negociado vazio não conta). */
export function hasPricedSelection(s: Partial<Selection> | undefined): boolean {
  return !!s && (!!s.selected || (!!s.negotiatedSelected && (s.negotiatedPrice ?? 0) > 0));
}

/**
 * Negociar para cima quase sempre é erro de digitação: destaca sem bloquear.
 * Só avisa quando o negociado está marcado, porque desmarcado ele não entra no total.
 */
export function isNegotiatedAboveQuoted(quote: QuotationMapSupplier): boolean {
  return (
    !!quote.negotiatedSelected &&
    quote.negotiatedPrice != null &&
    quote.price != null &&
    quote.negotiatedPrice > quote.price
  );
}

/** Dias até a data (negativo = já passou), contando por dia civil. */
export function daysUntil(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const target = new Date(raw);
  if (Number.isNaN(target.getTime())) return null;
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

export function validityStatus(days: number | null): 'expired' | 'expiring' | 'valid' | null {
  if (days === null) return null;
  if (days < 0) return 'expired';
  if (days <= 3) return 'expiring';
  return 'valid';
}

export function validityLabel(raw: string, days: number | null, locale: string): string {
  if (days === null) return raw ? `Validade: ${raw}` : 'Validade: --';
  const dateLabel = formatDate(raw, 'dd/MM/yyyy', locale);
  if (days < 0) return `Vencida em ${dateLabel}`;
  if (days === 0) return 'Vence hoje';
  if (days === 1) return 'Vence amanhã';
  if (days <= 3) return `Vence em ${days} dias`;
  return `Validade: ${dateLabel}`;
}

export function buildApprovalReview(
  source: ApprovalReviewSource,
  options: ApprovalReviewOptions
): ApprovalReviewData {
  const items = source.items ?? [];
  const supplierIds = source.supplierIds ?? [];
  const freights = source.supplierFreights;

  /** Primeiro valor preenchido do fornecedor entre os itens (nome, e-mail, validade...). */
  const supplierField = <K extends keyof QuotationMapSupplier>(id: string, field: K) => {
    for (const item of items) {
      const value = item.suppliers.get(id)?.[field];
      if (value) return value;
    }
    return undefined;
  };
  const supplierName = (id: string) => (supplierField(id, 'companyName') as string) || id;

  const hasSelectedProducts = (id: string) =>
    items.some(item => {
      const supplier = item.suppliers.get(id);
      return !!supplier && (supplier.selected || supplier.negotiatedSelected);
    });

  const selectedFreightValue = (id: string): number => {
    if (!hasSelectedProducts(id)) return 0;
    const freight = freights?.get(id);
    if (!freight) return 0;
    if (freight.negotiatedSelected) return freight.negotiatedFreight ?? freight.freight ?? 0;
    if (freight.selected) return freight.freight ?? 0;
    return 0;
  };

  const supplierTotal = (id: string): number =>
    items.reduce((total, item) => {
      const quote = item.suppliers.get(id);
      if (quote?.negotiatedSelected && quote.negotiatedPrice != null) {
        return total + quote.negotiatedPrice * item.quantity;
      }
      if (quote?.selected) return total + quote.price * item.quantity;
      return total;
    }, 0) + selectedFreightValue(id);

  /**
   * Concorrência do item: quantos fornecedores mandaram preço, qual o menor e quanto o preço
   * aprovado (`approvedUnit`, cotado ou negociado) fica acima dele. Compara com o preço cotado
   * dos outros fornecedores, que é o que cada um ofereceu.
   */
  const competition = (item: QuotationMapItem, approvedUnit: number): ApprovalReviewCompetition => {
    const offers = Array.from(item.suppliers.entries())
      .filter(([, quote]) => !quote.notParticipating && quote.price > 0)
      .map(([supplierId, quote]) => ({ supplierId, price: quote.price }));
    if (!offers.length) {
      return { quotes: 0, isBest: true, bestPrice: null, bestSupplierName: null, aboveBest: null };
    }
    const best = offers.reduce((a, b) => (b.price < a.price ? b : a));
    // Centavo de tolerância: arredondamento não pode tirar o "menor preço" de quem empatou.
    const isBest = approvedUnit <= best.price + 0.005;
    return {
      quotes: offers.length,
      isBest,
      bestPrice: best.price,
      bestSupplierName: supplierName(best.supplierId),
      aboveBest: isBest ? null : (approvedUnit - best.price) / best.price
    };
  };

  let aboveQuotedCount = 0;
  let negotiatedNotAppliedCount = 0;

  const reviewFreight = (id: string, freight: QuotationMapSupplierFreight): ApprovalReviewFreight => {
    const negotiated = freight.negotiatedFreight ? freight.negotiatedFreight : null;
    const useNegotiated = !!freight.negotiatedSelected && negotiated != null;
    const negotiatedNotApplied = negotiated != null && !useNegotiated;
    if (negotiatedNotApplied) negotiatedNotAppliedCount++;
    return {
      quoted: freight.freight,
      negotiated,
      useNegotiated,
      applied: selectedFreightValue(id),
      negotiatedNotApplied,
      saving: useNegotiated ? Math.max(0, freight.freight - (negotiated as number)) : 0
    };
  };

  const suppliers: ApprovalReviewSupplier[] = supplierIds
    .filter(hasSelectedProducts)
    .map(id => {
      const index = supplierIds.indexOf(id);
      const selectedQuotes: QuotationMapSupplier[] = [];
      const reviewItems = items
        .filter(item => hasPricedSelection(item.suppliers.get(id)))
        .map(item => {
          const quote = item.suppliers.get(id)!;
          selectedQuotes.push(quote);
          const useNegotiated = !!quote.negotiatedSelected && (quote.negotiatedPrice ?? 0) > 0;
          const unit = useNegotiated ? (quote.negotiatedPrice as number) : quote.price;
          const aboveQuoted = isNegotiatedAboveQuoted(quote);
          if (aboveQuoted) aboveQuotedCount++;
          // Negociado zerado é campo vazio: aparece como "—", não como R$ 0,00.
          const negotiatedPrice = quote.negotiatedPrice ? quote.negotiatedPrice : null;
          const negotiatedNotApplied = negotiatedPrice != null && !useNegotiated;
          if (negotiatedNotApplied) negotiatedNotAppliedCount++;
          return {
            code: item.code,
            product: item.product,
            branch: item.branch,
            quantity: item.quantity,
            unit: item.unit,
            price: quote.price,
            negotiatedPrice,
            useNegotiated,
            lineTotal: unit * item.quantity,
            aboveQuoted,
            negotiatedNotApplied,
            saving: useNegotiated ? Math.max(0, (quote.price - unit) * item.quantity) : 0,
            competition: competition(item, unit)
          };
        });

      const freight = freights?.get(id);
      const freightIncluded = !!freight && (freight.selected || !!freight.negotiatedSelected);
      const respondedAt = (supplierField(id, 'respondedAt') as string) || undefined;
      const validity = (supplierField(id, 'validity') as string) || '';
      const days = daysUntil(validity);
      const payment = [
        ...new Set(
          selectedQuotes
            .map(quote => [quote.paymentMethod, quote.paymentTerms].filter(Boolean).join(' · '))
            .filter(Boolean)
        )
      ];

      return {
        id,
        name: supplierName(id),
        email: (supplierField(id, 'email') as string) || '',
        accentColor: options.accentColor(Math.max(index, 0)),
        respondedAt,
        validityLabel: respondedAt ? validityLabel(validity, days, options.locale) : undefined,
        validityStatus: respondedAt ? validityStatus(days) : null,
        payment,
        items: reviewItems,
        freight: freight && freightIncluded ? reviewFreight(id, freight) : null,
        subtotal: supplierTotal(id)
      };
    })
    // Fornecedor só com negociado marcado sem valor não tem o que aprovar.
    .filter(supplier => supplier.items.length > 0);

  const unselectedItems = items
    .filter(item => !Array.from(item.suppliers.values()).some(hasPricedSelection))
    .map(item => ({ code: item.code, product: item.product, quantity: item.quantity, unit: item.unit }));

  const productsTotal = items.reduce(
    (total, item) =>
      total +
      Array.from(item.suppliers.values()).reduce((subtotal, s) => {
        const original = s.selected ? s.price * item.quantity : 0;
        const negotiated =
          s.negotiatedSelected && s.negotiatedPrice != null ? s.negotiatedPrice * item.quantity : 0;
        return subtotal + original + negotiated;
      }, 0),
    0
  );
  const freightTotal = supplierIds.reduce((total, id) => total + selectedFreightValue(id), 0);

  const productsSaving = items.reduce(
    (saving, item) =>
      saving +
      Array.from(item.suppliers.values()).reduce((itemSaving, s) => {
        if (s.negotiatedSelected && s.negotiatedPrice != null && s.price) {
          const difference = s.price - s.negotiatedPrice;
          return itemSaving + (difference > 0 ? difference * item.quantity : 0);
        }
        return itemSaving;
      }, 0),
    0
  );
  const freightSaving = supplierIds.reduce((saving, id) => {
    if (!hasSelectedProducts(id)) return saving;
    const freight = freights?.get(id);
    if (!freight?.negotiatedSelected || freight.negotiatedFreight == null) return saving;
    const difference = freight.freight - freight.negotiatedFreight;
    return saving + (difference > 0 ? difference : 0);
  }, 0);

  const reviewedItems = suppliers.flatMap(supplier => supplier.items);
  const quotesSum = reviewedItems.reduce((sum, item) => sum + item.competition.quotes, 0);

  return {
    purchaseId: source.purchaseId,
    itemsTotal: items.length,
    selectedItemsCount: items.filter(item =>
      Array.from(item.suppliers.values()).some(s => s.selected || s.negotiatedSelected)
    ).length,
    totalValue: productsTotal + freightTotal,
    totalSaving: productsSaving + freightSaving,
    suppliers,
    unselectedItems,
    hasExpired: suppliers.some(s => s.validityStatus === 'expired'),
    aboveQuotedCount,
    negotiatedNotAppliedCount,
    bestPriceCount: reviewedItems.filter(item => item.competition.isBest).length,
    averageQuotes: reviewedItems.length ? quotesSum / reviewedItems.length : 0,
    singleQuoteCount: reviewedItems.filter(item => item.competition.quotes === 1).length
  };
}

/**
 * Converte as cotações aguardando aprovação na mesma seleção que o comprador fez no mapa,
 * para a revisão sair igual nas duas telas.
 *
 * Ao mandar para aprovação, o backend grava o negociado como preço da oferta e guarda o
 * cotado em `originalPrice` (o `oldPrice` do produto). Com os dois, o item volta a ser
 * "cotado X, negociado Y". O frete que chega já é o que foi aprovado (cotado ou negociado).
 */
export function selecaoDaAprovacao(
  source: ApprovalReviewSource,
  winners: Record<string, string[]>
): ApprovalReviewSource {
  const winnerSuppliers = new Set<string>();

  const items = source.items.map(item => {
    const winnerIds = winners[item.code] ?? [];
    const suppliers = new Map<string, QuotationMapSupplier>();
    for (const [id, quote] of item.suppliers) {
      const isWinner = !!quote.offerId && winnerIds.includes(quote.offerId);
      if (!isWinner) {
        suppliers.set(id, { ...quote, selected: false, negotiatedSelected: false, negotiatedPrice: null });
        continue;
      }
      winnerSuppliers.add(id);
      const quoted = quote.originalPrice;
      const negotiated = quoted != null && quoted > 0 && quoted !== quote.price;
      suppliers.set(
        id,
        negotiated
          ? { ...quote, price: quoted, negotiatedPrice: quote.price, negotiatedSelected: true, selected: false }
          : { ...quote, selected: true, negotiatedSelected: false, negotiatedPrice: null }
      );
    }
    return { ...item, suppliers };
  });

  const supplierFreights = new Map<string, QuotationMapSupplierFreight>();
  for (const [id, freight] of source.supplierFreights ?? []) {
    supplierFreights.set(id, {
      ...freight,
      selected: winnerSuppliers.has(id),
      negotiatedSelected: false,
      negotiatedFreight: null
    });
  }

  return { ...source, items, supplierFreights };
}
